import React, { useEffect, useState } from "react";
import {
  Button,
  Center,
  Group,
  Loader,
  Text,
  TextInput,
  Textarea,
  Title,
  useMantineColorScheme,
  Container,
  Badge,
  Paper,
  Divider,
  Modal,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import {
  IconArrowLeft,
  IconCalendar,
  IconPlayerPlay,
  IconCheck,
  IconArchive,
  IconSend,
} from "@tabler/icons-react";
import {
  useAdminSeasonByIdQuery,
  useUpdateSeasonMutation,
  usePublishSeasonMutation,
  useActivateSeasonMutation,
  useConcludeSeasonMutation,
  useArchiveSeasonMutation,
  SeasonStatus,
} from "generated/graphql";

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "gray",
  SCHEDULED: "blue",
  ACTIVE: "green",
  CONCLUDED: "orange",
  ARCHIVED: "red",
};

const STATUS_DESCRIPTIONS: Record<string, string> = {
  DRAFT: "Season is being configured and not visible to users.",
  SCHEDULED: "Season is scheduled and visible but not yet active.",
  ACTIVE: "Season is currently active. Only one season can be active at a time.",
  CONCLUDED: "Season has concluded but is still visible for historical purposes.",
  ARCHIVED: "Season is archived and hidden from normal views.",
};

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * SeasonEdit
 *
 * Admin page for viewing and editing a season.
 * Editing is only allowed for Draft and Scheduled seasons.
 * Status transitions are handled via dedicated buttons.
 */
export default function SeasonEdit() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Queries and mutations
  const { data, loading, error, refetch } = useAdminSeasonByIdQuery({
    variables: { id: seasonId! },
    skip: !seasonId,
    fetchPolicy: "cache-and-network",
  });

  const [updateSeason, { loading: saving }] = useUpdateSeasonMutation();
  const [publishSeason, { loading: publishing }] = usePublishSeasonMutation();
  const [activateSeason, { loading: activating }] = useActivateSeasonMutation();
  const [concludeSeason, { loading: concluding }] = useConcludeSeasonMutation();
  const [archiveSeason, { loading: archiving }] = useArchiveSeasonMutation();

  // Local state
  const [displayName, setDisplayName] = useState("");
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState<Date | null>(null);
  const [endsAt, setEndsAt] = useState<Date | null>(null);
  const [visibleFrom, setVisibleFrom] = useState<Date | null>(null);
  const [visibleTo, setVisibleTo] = useState<Date | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Modal for confirmation
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, action: "", title: "", message: "", onConfirm: () => {} });

  const season = data?.season;
  const canEdit = season?.status === "DRAFT" || season?.status === "SCHEDULED";

  // Populate form when data loads
  useEffect(() => {
    if (season) {
      setDisplayName(season.displayName);
      setTag(season.tag);
      setDescription(season.description ?? "");
      setStartsAt(season.startsAt ? new Date(season.startsAt) : null);
      setEndsAt(season.endsAt ? new Date(season.endsAt) : null);
      setVisibleFrom(season.visibleFrom ? new Date(season.visibleFrom) : null);
      setVisibleTo(season.visibleTo ? new Date(season.visibleTo) : null);
      setHasChanges(false);
    }
  }, [season]);

  // Track changes
  useEffect(() => {
    if (season) {
      const changed =
        displayName !== season.displayName ||
        tag !== season.tag ||
        description !== (season.description ?? "") ||
        startsAt?.toISOString() !== season.startsAt ||
        endsAt?.toISOString() !== season.endsAt ||
        visibleFrom?.toISOString() !== season.visibleFrom ||
        visibleTo?.toISOString() !== season.visibleTo;
      setHasChanges(changed);
    }
  }, [displayName, tag, description, startsAt, endsAt, visibleFrom, visibleTo, season]);

  async function handleSave() {
    if (!seasonId || !canEdit) return;

    try {
      const result = await updateSeason({
        variables: {
          input: {
            seasonId: seasonId,
            displayName: displayName.trim() || undefined,
            tag: tag.trim() || undefined,
            description: description.trim() || undefined,
            startsAt: startsAt?.toISOString(),
            endsAt: endsAt?.toISOString(),
            visibleFrom: visibleFrom?.toISOString(),
            visibleTo: visibleTo?.toISOString(),
          },
        },
      });

      if (result.data?.updateSeason) {
        notifications.show({
          title: "Success",
          message: "Season updated successfully",
          color: "green",
        });
        refetch();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to update season",
        color: "red",
      });
    }
  }

  async function handlePublish() {
    if (!seasonId) return;
    try {
      const result = await publishSeason({ variables: { id: seasonId } });
      if (result.data?.publishSeason) {
        notifications.show({
          title: "Published",
          message: "Season is now scheduled and visible to users",
          color: "blue",
        });
        refetch();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to publish season",
        color: "red",
      });
    }
    setConfirmModal({ ...confirmModal, open: false });
  }

  async function handleActivate() {
    if (!seasonId) return;
    try {
      const result = await activateSeason({ variables: { id: seasonId } });
      if (result.data?.activateSeason) {
        notifications.show({
          title: "Activated",
          message: "Season is now active!",
          color: "green",
        });
        refetch();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to activate season",
        color: "red",
      });
    }
    setConfirmModal({ ...confirmModal, open: false });
  }

  async function handleConclude() {
    if (!seasonId) return;
    try {
      const result = await concludeSeason({ variables: { id: seasonId } });
      if (result.data?.concludeSeason) {
        notifications.show({
          title: "Concluded",
          message: "Season has been concluded",
          color: "orange",
        });
        refetch();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to conclude season",
        color: "red",
      });
    }
    setConfirmModal({ ...confirmModal, open: false });
  }

  async function handleArchive() {
    if (!seasonId) return;
    try {
      const result = await archiveSeason({ variables: { id: seasonId } });
      if (result.data?.archiveSeason) {
        notifications.show({
          title: "Archived",
          message: "Season has been archived",
          color: "gray",
        });
        refetch();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to archive season",
        color: "red",
      });
    }
    setConfirmModal({ ...confirmModal, open: false });
  }

  if (loading && !data) {
    return (
      <Center style={{ height: "calc(100dvh - 48px)" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !season) {
    return (
      <Container size="xl" px="lg" py="lg">
        <Group justify="flex-start" mb="sm">
          <Button
            variant="filled"
            color="blue"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/admin-hub/seasons")}
          >
            Back to Seasons
          </Button>
        </Group>
        <Text c="red">Failed to load season data.</Text>
      </Container>
    );
  }

  const isProcessing = publishing || activating || concluding || archiving;

  return (
    <Container size="xl" px="lg" py="lg">
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub/seasons")}
          size="sm"
        >
          Back to Seasons
        </Button>
      </Group>

      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Title c={isDark ? "white" : "black"}>
              Season {season.seasonNumber}: {season.displayName}
            </Title>
            <Text c={isDark ? "gray.5" : "gray.6"} size="sm" mt="xs">
              Tag: {season.tag} | Slug: {season.slug}
            </Text>
          </div>
          <Badge
            color={STATUS_COLORS[season.status] || "gray"}
            variant="filled"
            size="lg"
          >
            {season.status}
          </Badge>
        </div>

        {/* Status Info */}
        <Paper withBorder p="md" radius="md">
          <Text size="sm" c={isDark ? "gray.4" : "gray.6"}>
            {STATUS_DESCRIPTIONS[season.status] || "Unknown status"}
          </Text>
        </Paper>

        {/* Status Transitions */}
        <Paper withBorder p="md" radius="md">
          <Text fw={500} mb="md">
            Status Transitions
          </Text>
          <Group>
            {season.status === "DRAFT" && (
              <Button
                leftSection={<IconSend size={16} />}
                color="blue"
                onClick={() =>
                  setConfirmModal({
                    open: true,
                    action: "publish",
                    title: "Publish Season?",
                    message:
                      "Publishing will make this season visible to users as 'Scheduled'. Continue?",
                    onConfirm: handlePublish,
                  })
                }
                loading={publishing}
                disabled={isProcessing}
              >
                Publish
              </Button>
            )}
            {season.status === "SCHEDULED" && (
              <Button
                leftSection={<IconPlayerPlay size={16} />}
                color="green"
                onClick={() =>
                  setConfirmModal({
                    open: true,
                    action: "activate",
                    title: "Activate Season?",
                    message:
                      "Activating will make this the current season. Only one season can be active at a time. Continue?",
                    onConfirm: handleActivate,
                  })
                }
                loading={activating}
                disabled={isProcessing}
              >
                Activate
              </Button>
            )}
            {season.status === "ACTIVE" && (
              <Button
                leftSection={<IconCheck size={16} />}
                color="orange"
                onClick={() =>
                  setConfirmModal({
                    open: true,
                    action: "conclude",
                    title: "Conclude Season?",
                    message:
                      "Concluding will end the active season. This action cannot be undone. Continue?",
                    onConfirm: handleConclude,
                  })
                }
                loading={concluding}
                disabled={isProcessing}
              >
                Conclude
              </Button>
            )}
            {season.status === "CONCLUDED" && (
              <Button
                leftSection={<IconArchive size={16} />}
                color="gray"
                onClick={() =>
                  setConfirmModal({
                    open: true,
                    action: "archive",
                    title: "Archive Season?",
                    message:
                      "Archiving will hide this season from normal views. Continue?",
                    onConfirm: handleArchive,
                  })
                }
                loading={archiving}
                disabled={isProcessing}
              >
                Archive
              </Button>
            )}
            {season.status === "ARCHIVED" && (
              <Text size="sm" c="dimmed">
                This season is archived. No further transitions available.
              </Text>
            )}
          </Group>
        </Paper>

        <Divider />

        {/* Edit Form */}
        <div>
          <Title order={3} c={isDark ? "white" : "black"} mb="md">
            Season Details
          </Title>
          {!canEdit && (
            <Paper withBorder p="sm" mb="md" bg="yellow.1">
              <Text size="sm" c="yellow.9">
                Editing is only allowed for Draft and Scheduled seasons.
              </Text>
            </Paper>
          )}

          <div className="space-y-4">
            <TextInput
              label="Display Name"
              placeholder="Spring 2025"
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={!canEdit}
            />

            <TextInput
              label="Tag"
              placeholder="S25-1"
              value={tag}
              onChange={(e) => setTag(e.currentTarget.value)}
              disabled={!canEdit}
            />

            <Textarea
              label="Description"
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              minRows={3}
              disabled={!canEdit}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DateTimePicker
                label="Start Date"
                value={startsAt}
                onChange={setStartsAt}
                leftSection={<IconCalendar size={16} />}
                disabled={!canEdit}
                clearable
              />

              <DateTimePicker
                label="End Date"
                value={endsAt}
                onChange={setEndsAt}
                leftSection={<IconCalendar size={16} />}
                disabled={!canEdit}
                clearable
                minDate={startsAt || undefined}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DateTimePicker
                label="Visible From"
                value={visibleFrom}
                onChange={setVisibleFrom}
                leftSection={<IconCalendar size={16} />}
                disabled={!canEdit}
                clearable
              />

              <DateTimePicker
                label="Visible Until"
                value={visibleTo}
                onChange={setVisibleTo}
                leftSection={<IconCalendar size={16} />}
                disabled={!canEdit}
                clearable
              />
            </div>

            {canEdit && (
              <Group mt="xl">
                <Button
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  loading={saving}
                >
                  Save Changes
                </Button>
                <Button
                  variant="light"
                  color="gray"
                  onClick={() => {
                    if (season) {
                      setDisplayName(season.displayName);
                      setTag(season.tag);
                      setDescription(season.description ?? "");
                      setStartsAt(season.startsAt ? new Date(season.startsAt) : null);
                      setEndsAt(season.endsAt ? new Date(season.endsAt) : null);
                      setVisibleFrom(
                        season.visibleFrom ? new Date(season.visibleFrom) : null
                      );
                      setVisibleTo(
                        season.visibleTo ? new Date(season.visibleTo) : null
                      );
                      setHasChanges(false);
                    }
                  }}
                  disabled={!hasChanges}
                >
                  Reset
                </Button>
              </Group>
            )}
          </div>
        </div>

        {/* Timestamps */}
        <Paper withBorder p="md" radius="md" mt="xl">
          <Text size="sm" c="dimmed">
            Created: {formatDateTime(season.createdAt)} | Last Updated:{" "}
            {formatDateTime(season.updatedAt)}
          </Text>
        </Paper>
      </div>

      {/* Confirmation Modal */}
      <Modal
        opened={confirmModal.open}
        onClose={() => setConfirmModal({ ...confirmModal, open: false })}
        title={confirmModal.title}
        centered
      >
        <Text size="sm" mb="lg">
          {confirmModal.message}
        </Text>
        <Group justify="flex-end">
          <Button
            variant="light"
            color="gray"
            onClick={() => setConfirmModal({ ...confirmModal, open: false })}
          >
            Cancel
          </Button>
          <Button
            color={
              confirmModal.action === "publish"
                ? "blue"
                : confirmModal.action === "activate"
                ? "green"
                : confirmModal.action === "conclude"
                ? "orange"
                : "gray"
            }
            onClick={confirmModal.onConfirm}
            loading={isProcessing}
          >
            Confirm
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}
